import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Button,
  ListItemButton,
} from '@mui/material';
import {
  Info,
  Warning,
  CheckCircle,
  Error,
  Delete,
  MarkEmailRead,
  Circle,
  DoneAll,
} from '@mui/icons-material';
import { notificationService } from '../services/notification.service';
import { requestService } from '../services/requestService';
import RequestDetailsDialog from '../components/request/RequestDetailsDialog';
import { dispatchNotificationsUpdated } from '../utils/notificationEvents';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notification-tabpanel-${index}`}
      aria-labelledby={`notification-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

function getRequestIdFromNotification(notification: any): number | null {
  const metadata = notification?.metadata;
  if (metadata?.requestId) return Number(metadata.requestId);

  const link = notification?.link || '';
  const match = link.match(/\/requests\/(\d+)/);
  return match ? Number(match[1]) : null;
}

const Notifications: React.FC = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [viewingRequest, setViewingRequest] = useState<any>(null);
  const [loadingRequest, setLoadingRequest] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationService.getAll();
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      await fetchNotifications();
      dispatchNotificationsUpdated();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleMarkAsRead = async (id: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    try {
      await notificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n))
      );
      dispatchNotificationsUpdated();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleDelete = async (id: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    try {
      await notificationService.delete(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      dispatchNotificationsUpdated();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const openRequestModal = async (notification: any) => {
    const requestId = getRequestIdFromNotification(notification);
    if (!requestId) return;

    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }

    try {
      setLoadingRequest(true);
      setRequestModalOpen(true);
      const response = await requestService.getById(requestId);
      setViewingRequest(response.data);
    } catch (error) {
      console.error('Error loading request for notification:', error);
      setViewingRequest(null);
    } finally {
      setLoadingRequest(false);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'info':
        return <Info color="info" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'success':
        return <CheckCircle color="success" />;
      case 'error':
        return <Error color="error" />;
      default:
        return <Info />;
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('notifications.justNow');
    if (minutes < 60) return `${minutes} ${t('notifications.minutesAgo')}`;
    if (hours < 24) return `${hours} ${t('notifications.hoursAgo')}`;
    return `${days} ${t('notifications.daysAgo')}`;
  };

  const renderNotificationItem = (notification: any, showUnreadDot = false) => {
    const requestId = getRequestIdFromNotification(notification);
    const isClickable = Boolean(requestId);

    return (
      <ListItem
        key={notification.id}
        disablePadding
        secondaryAction={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {!notification.read && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DoneAll fontSize="small" />}
                onClick={(e) => handleMarkAsRead(notification.id, e)}
              >
                {t('notifications.markRead')}
              </Button>
            )}
            <IconButton edge="end" aria-label="delete" onClick={(e) => handleDelete(notification.id, e)}>
              <Delete />
            </IconButton>
          </Box>
        }
        sx={{
          bgcolor: notification.read ? 'transparent' : 'action.hover',
        }}
      >
        <ListItemButton
          onClick={() => isClickable && openRequestModal(notification)}
          disabled={!isClickable}
          sx={{ pr: !notification.read ? 18 : 8 }}
        >
          <ListItemIcon>{getIcon(notification.type)}</ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {(showUnreadDot || !notification.read) && (
                  <Circle sx={{ fontSize: 8, color: 'primary.main' }} />
                )}
                <Typography variant="body1" fontWeight={notification.read ? 'normal' : 'bold'}>
                  {notification.title}
                </Typography>
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {notification.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getTimeAgo(notification.createdAt)}
                </Typography>
              </Box>
            }
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const unreadNotifications = notifications.filter((n) => !n.read);
  const readNotifications = notifications.filter((n) => n.read);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('notifications.title')}
        </Typography>
        <Button variant="outlined" startIcon={<MarkEmailRead />} onClick={handleMarkAllAsRead}>
          {t('notifications.markAllRead')}
        </Button>
      </Box>

      <Card elevation={2}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} aria-label="notification tabs">
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('notifications.all')}
                  <Chip label={notifications.length} size="small" />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('notifications.unread')}
                  <Chip label={unreadNotifications.length} size="small" color="primary" />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('notifications.read')}
                  <Chip label={readNotifications.length} size="small" />
                </Box>
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <List>
            {!loading && notifications.length === 0 && (
              <ListItem>
                <ListItemText primary={t('notifications.caughtUp')} />
              </ListItem>
            )}
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                {renderNotificationItem(notification, !notification.read)}
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <List>
            {unreadNotifications.length > 0 ? (
              unreadNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  {renderNotificationItem(notification, true)}
                  {index < unreadNotifications.length - 1 && <Divider />}
                </React.Fragment>
              ))
            ) : (
              <ListItem>
                <ListItemText primary={t('notifications.noUnread')} />
              </ListItem>
            )}
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <List>
            {readNotifications.length > 0 ? (
              readNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  {renderNotificationItem(notification)}
                  {index < readNotifications.length - 1 && <Divider />}
                </React.Fragment>
              ))
            ) : (
              <ListItem>
                <ListItemText primary={t('notifications.noRead')} />
              </ListItem>
            )}
          </List>
        </TabPanel>
      </Card>

      <RequestDetailsDialog
        open={requestModalOpen}
        request={viewingRequest}
        loading={loadingRequest}
        onClose={() => {
          setRequestModalOpen(false);
          setViewingRequest(null);
        }}
      />
    </Box>
  );
};

export default Notifications;
